import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { FaArrowRight, FaCheckCircle, FaLock, FaEnvelope, FaEye, FaEyeSlash } from 'react-icons/fa';
import { GrInstagram } from "react-icons/gr";
import { useDispatch, useSelector } from 'react-redux';
import { setError, setLoading, setToken, saveUserDetails } from '../../Redux/userSlice';  // Assuming these actions exist in the slice
import LoadingSpinner from '../../components/LoadingSpinner';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { motion } from "framer-motion";
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const dispatch = useDispatch();
  const { error, loading } = useSelector((state) => state.user);
  const location = useLocation();
  
  // Get redirectTo parameter from URL; use influencer base when on /influencer/login
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirectTo');
  const isInfluencerPortal = location.pathname.startsWith('/influencer');
  const basePath = isInfluencerPortal ? '/influencer' : '';

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!email || !password) {
      dispatch(setError('Please enter both email and password'));
      return;
    }

    if (!email.includes('@')) {
      dispatch(setError('Please enter a valid email address'));
      return;
    }

    try {
      setEmailLoading(true);
      dispatch(setError(null)); // Clear any previous errors

      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          password
        })
      });

      const data = await res.json();
      console.log('Login response:', data); // Debug log

      if (res.ok && data.success) {
        // Store token in localStorage
        if (data.token) {
          localStorage.setItem('token', data.token);
          dispatch(setToken(data.token));
        }

        // Store user data in Redux and localStorage
        if (data.user) {
          dispatch(saveUserDetails(data.user));
          localStorage.setItem('userData', JSON.stringify(data.user));
          localStorage.setItem('userType', 'influancer');
          // Store user ID separately for easy access
          localStorage.setItem('userId', data.user._id);
        }

        // Redirect based on profile completion and redirectTo parameter
        const dashboardPath = `${basePath}/dashboard`;
        const userDetailsPath = `${basePath}/user-details`;
        if (data.user?.isProfileComplete) {
          if (redirectTo) {
            window.location.href = redirectTo;
          } else {
            window.location.href = dashboardPath;
          }
        } else {
          if (redirectTo) {
            window.location.href = `${userDetailsPath}?redirectTo=${encodeURIComponent(redirectTo)}`;
          } else {
            window.location.href = userDetailsPath;
          }
        }
      } else {
        // Handle specific error cases
        const errorMessage = data.error || 'Login failed. Please try again.';
        dispatch(setError(errorMessage));
        setPassword(''); // Clear password on error
      }
    } catch (err) {
      console.error('Login error:', err);
      dispatch(setError('Network error. Please check your connection and try again.'));
      setPassword(''); // Clear password on error
    } finally {
      setEmailLoading(false);
    }
  };

  const handleInstagramLogin = async () => {
    try {
      dispatch(setLoading(true));

      // Track Instagram login button click (non-blocking)
      // This happens BEFORE OAuth redirect to ensure analytics are captured
      try {
        const deviceFingerprint = await getDeviceFingerprint();
        // Fire analytics request (don't await - non-blocking)
        fetch(`${process.env.REACT_APP_API_URL}/auth/analytics/instagram-login-click`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ deviceFingerprint }),
          // Use sendBeacon for better reliability if available
        }).catch(err => {
          // Silently fail - analytics should not block OAuth flow
          console.error('Analytics tracking failed (non-blocking):', err);
        });
      } catch (analyticsError) {
        // Silently fail - analytics should not block OAuth flow
        console.error('Analytics error (non-blocking):', analyticsError);
      }

      // Include redirectTo in the request if it exists
      const url = redirectTo 
        ? `${process.env.REACT_APP_API_URL}/auth/instagram-login?redirectTo=${encodeURIComponent(redirectTo)}`
        : `${process.env.REACT_APP_API_URL}/auth/instagram-login`;
      
      const response = await fetch(url);
      const data = await response.json();

      if (data.authUrl) {
        // Add a small delay to ensure loading state is set before redirect
        setTimeout(() => {
          window.location.assign(data.authUrl);
        }, 100);
      } else {
        // Only reset loading and show error if no auth URL is returned
        dispatch(setError('Failed to initialize Instagram login'));
        dispatch(setLoading(false));
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch(setError('Something went wrong!'));
      dispatch(setLoading(false)); // Only reset loading on error
    }
    // Removed the finally block to prevent loading state reset before redirect
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <LoadingSpinner message="Connecting with Instagram..." />
      </>
    )

  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white flex flex-col">
      <div className="absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
      </div>
      <Navbar />

      <div className="relative flex-1 flex items-center justify-center px-2 md:px-4 py-6 md:py-12">
        <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center gap-12">
          {/* Left side - Registration Info and Why Connect section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full lg:w-1/2 relative"
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.03] p-3 md:p-6 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
              <div className="absolute -top-20 -right-24 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" />
              <div className="absolute -bottom-24 -left-28 h-60 w-60 rounded-full bg-blue-500/15 blur-3xl" />
              <div className="relative">
              <div className="mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-6">Welcome to Infuber</h2>
                <div className="rounded-xl bg-orange-500/10 p-6 mb-6 ring-1 ring-orange-500/25">
                  <h2 className="text-lg md:text-xl font-semibold text-orange-500 mb-3">Getting Started</h2>
                  <p className="text-gray-200 text-xs md:text-lg leading-relaxed">
                    To begin your journey with Infuber, you'll need to first register using your Instagram account.
                    This one-time setup allows us to verify your identity and connect your social presence.
                  </p>
                  <p className="text-gray-200 text-xs md:text-lg leading-relaxed mt-4">
                    After registration, you can conveniently log in using your email and password for future access.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-xl md:text-2xl font-semibold text-orange-500">Why Connect?</h2>
                <ul className="space-y-4">
                  {[
                    "Certified and tested by Meta",
                    "Get detailed Instagram insights",
                    "Engage with comments and messages",
                    "Schedule posts and manage content efficiently"
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start text-white/70 group"
                    >
                      <FaCheckCircle className="text-orange-500 mr-3 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="text-xs md:text-lg">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              </div>
            </div>
          </motion.div>

          {/* Right side - Login form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full lg:w-1/2 max-w-md"
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.03] p-3 md:p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
              <div className="absolute -top-20 -left-24 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" />
              <div className="absolute -bottom-24 -right-28 h-60 w-60 rounded-full bg-blue-500/15 blur-3xl" />
              <div className="relative">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-4 text-center">Sign In</h2>
              <p className="text-white/70 text-xs md:text-sm text-center mb-8">
                Connect your Instagram account to unlock powerful insights and grow your influence.
              </p>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg px-2 md:px-4 py-2 md:py-3 mb-6 text-xs md:text-sm"
                >
                  {error}
                </motion.div>
              )}

              <button
                onClick={handleInstagramLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-2 md:py-4 px-2 md:px-6 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-300 text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <GrInstagram className="text-xl md:text-2xl" />
                <span>Continue with Instagram</span>
                <FaArrowRight className="ml-auto" />
              </button>

              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs md:text-sm">
                  <span className="px-4 bg-black/70 text-white/50 ring-1 ring-white/10 rounded-full py-1">or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-6">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-white/50" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-2 md:pr-4 py-2 md:py-3 bg-white/5 ring-1 ring-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500/70 focus:ring-offset-0 transition-all"
                    placeholder="Email address"
                    required
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 md:pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-white/50" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-2 md:pr-12 py-2 md:py-3 bg-white/5 ring-1 ring-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500/70 focus:ring-offset-0 transition-all"
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-2 md:pr-3 flex items-center text-white/50 hover:text-white/80 transition-colors"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`w-full py-2 md:py-4 px-2 md:px-6 rounded-xl transition-all duration-300 text-white font-semibold shadow-lg transform ${emailLoading
                      ? 'bg-orange-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl hover:-translate-y-0.5'
                    }`}
                >
                  {emailLoading ? 'Signing in...' : 'Sign in with Email'}
                </button>

                <div className="text-center">
                  <a
                    href="/forgot-password"
                    className="inline-block text-orange-500 hover:text-orange-400 text-xs md:text-sm font-medium transition-colors duration-200 hover:underline"
                  >
                    Forgot your password?
                  </a>
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-300">
                    <b className="text-orange-500">Note:</b> We respect your <b>privacy</b> and we don't share or store any personal <b>information</b>.
                  </p>
                </div>
              </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
export default Login;